import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listAdvertisers } from "@/api/commerce";
import { listAffiliates } from "@/api/commerce";
import type { Advertiser, Affiliate } from "@/types/commerce";

/**
 * Hook: fetch the full advertiser list once and expose lookup helpers.
 *
 * Sharing one query key across every list page lets react-query dedupe and
 * cache the result, so adding the Advertiser column to N pages costs one
 * network call total.
 */
export function useAdvertiserLookup(): {
    advertisers: Advertiser[];
    loaded: boolean;
    byId: (id: number | null | undefined) => Advertiser | null;
} {
    const { data } = useQuery({
        queryKey: ["advertisers", { includeArchived: false }],
        queryFn: () => listAdvertisers({ includeArchived: false }),
        staleTime: 60_000,
    });
    const advertisers = data ?? [];
    const byId = React.useCallback(
        (id: number | null | undefined): Advertiser | null => {
            if (id == null) return null;
            return advertisers.find((a) => a.id === id) ?? null;
        },
        [advertisers],
    );
    return { advertisers, loaded: data != null, byId };
}

/**
 * Same pattern for affiliates — used by campaigns + ad-sources + leads.
 */
export function useAffiliateLookup(): {
    affiliates: Affiliate[];
    loaded: boolean;
    byId: (id: number | null | undefined) => Affiliate | null;
} {
    const { data } = useQuery({
        queryKey: ["affiliates", { includeArchived: false }],
        queryFn: () => listAffiliates({ includeArchived: false }),
        staleTime: 60_000,
    });
    const affiliates = data ?? [];
    const byId = React.useCallback(
        (id: number | null | undefined): Affiliate | null => {
            if (id == null) return null;
            return affiliates.find((a) => a.id === id) ?? null;
        },
        [affiliates],
    );
    return { affiliates, loaded: data != null, byId };
}

/**
 * Cell renderer for an "Advertiser" column. Renders a compact link to
 * /advertisers/:id when an advertiser is set, or an em dash when not.
 * Falls back to "#<id>" if the advertiser list hasn't loaded yet (or the
 * id is for an archived row not in the active list) so the column never
 * shows a broken placeholder.
 */
export function AdvertiserCell({
    advertiserId,
    lookup,
}: {
    advertiserId: number | null | undefined;
    lookup: (id: number | null | undefined) => Advertiser | null;
}): React.ReactNode {
    if (advertiserId == null) {
        return <span className="text-muted-foreground">—</span>;
    }
    const advertiser = lookup(advertiserId);
    return (
        <Link
            to={`/advertisers/${advertiserId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm hover:underline"
            data-testid={`advertiser-cell-${advertiserId}`}
        >
            {advertiser ? advertiser.name : `#${advertiserId}`}
        </Link>
    );
}

/** Symmetric affiliate cell renderer. */
export function AffiliateCell({
    affiliateId,
    lookup,
}: {
    affiliateId: number | null | undefined;
    lookup: (id: number | null | undefined) => Affiliate | null;
}): React.ReactNode {
    if (affiliateId == null) {
        return <span className="text-muted-foreground">—</span>;
    }
    const affiliate = lookup(affiliateId);
    return (
        <Link
            to={`/affiliates/${affiliateId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm hover:underline"
            data-testid={`affiliate-cell-${affiliateId}`}
        >
            {affiliate ? affiliate.name : `#${affiliateId}`}
        </Link>
    );
}
